import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { siteConfig } from "@/config/site";

import { useMemo, useState } from "react";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { GLOSSARY, GLOSSARY_CATEGORIES, listGlossary } from "@/content/glossary";

const GLOSSARY_FAQS = [
  {
    q: "How is a glossary term different from a dictionary definition?",
    a: "Each entry pairs a plain-language definition with a worked example from actual SEO work, plus cross-references to related terms — closer to a technical reference than a dictionary listing.",
  },
  {
    q: "Why do some terms include an FAQ and others don't?",
    a: "Terms that generate genuine practitioner confusion — like the difference between AEO and GEO, or E-E-A-T and domain authority — carry an FAQ section. Straightforward terms don't need one padded on artificially.",
  },
  {
    q: "How often are definitions reviewed?",
    a: "We review the glossary whenever a term's real-world meaning shifts — for example, when Google formalised guidance on AI Overviews citations, or when Core Web Vitals metrics changed. Stale definitions get rewritten, not just re-dated.",
  },
  {
    q: "Do you cover AI-search terminology like AEO and GEO?",
    a: "Yes, deliberately. Many general SEO glossaries haven't caught up to answer-engine and generative-engine terminology; ours treats it as core vocabulary, not an addendum.",
  },
  {
    q: "Can I search for a term instead of browsing A–Z?",
    a: "The search box above filters by term name and short definition in real time, and the category filters narrow the list to a single discipline such as technical, local, or analytics.",
  },
  {
    q: "Are these definitions specific to Google, or search generally?",
    a: "Most terms apply across search engines and AI answer engines; where a definition is Google-specific — a particular ranking system or SERP feature — the entry says so explicitly.",
  },
  {
    q: "Who writes and maintains the glossary?",
    a: "The same practitioners who write the guides and blog maintain the glossary, which is why definitions include the same worked examples and cross-links rather than generic textbook phrasing.",
  },
];

export const Route = createFileRoute("/glossary/")({
  head: () => ({ ...pageMeta({
    path: "/glossary/",
    title: `SEO Glossary — 36 Modern SEO, AEO & GEO Terms Defined | ${siteConfig.name}`,
    description:
      `A working glossary of modern SEO from ${siteConfig.name} — 36 practitioner-defined terms across AI search, technical, on-page, off-page, local, analytics, and content, with examples and cross-references.`,
    keywords: [
      "SEO glossary",
      "SEO terms",
      "AEO definition",
      "GEO definition",
      "E-E-A-T",
      "Core Web Vitals",
      "schema.org glossary",
      "SEO dictionary",
    ],
  }), scripts: ldScripts(
    breadcrumbLd([{ name: "Home", path: "/" }, { name: "Glossary", path: "/glossary/" }]),
    faqLd(GLOSSARY_FAQS),
  ) }),
  component: GlossaryIndex,
});

function GlossaryIndex() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<"All" | typeof GLOSSARY_CATEGORIES[number]>("All");

  const results = useMemo(() => {
    const base = listGlossary();
    const filtered = base.filter((t) => {
      if (cat !== "All" && t.category !== cat) return false;
      if (q && !(`${t.term} ${t.short}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
    return filtered;
  }, [q, cat]);

  // Group A–Z
  const grouped = useMemo(() => {
    const map = new Map<string, typeof GLOSSARY>();
    results.forEach((t) => {
      const letter = t.term[0].toUpperCase().match(/[A-Z]/) ? t.term[0].toUpperCase() : "#";
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter)!.push(t);
    });
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [results]);

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-24 pb-10">
          <EyebrowLabel>Glossary</EyebrowLabel>
          <h1 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            The words we
            <br />
            <span className="text-primary">actually use.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            {GLOSSARY.length} terms defined by practitioners — including AI-search vocabulary you
            won't find in older references. Every definition includes an example and, where
            useful, cross-links.
          </p>
        </section>

        <section className="container-page pb-10">
          <div className="flex flex-col md:flex-row gap-4 md:items-center">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search terms…"
              className="flex-1 min-w-0 rounded-full bg-background border border-input px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex flex-wrap gap-2">
              {(["All", ...GLOSSARY_CATEGORIES] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium tracking-wide transition-colors border ${
                    cat === c
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="container-page pb-32">
          {grouped.length === 0 && (
            <p className="text-muted-foreground">No terms match.</p>
          )}
          {grouped.map(([letter, terms]) => (
            <div key={letter} className="mt-10 first:mt-0">
              <h2 className="text-xs uppercase tracking-widest text-primary border-b border-border pb-2 mb-4">
                {letter}
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {terms.map((t) => (
                  <Link
                    key={t.slug}
                    to="/glossary/$slug/"
                    params={{ slug: t.slug }}
                    className="group rounded-xl border border-border p-5 bg-card/30 hover:bg-card/50 hover:border-primary/40 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-xl tracking-tight">{t.term}</h3>
                      <span className="shrink-0 text-[10px] uppercase tracking-widest text-muted-foreground border border-border rounded-full px-2 py-0.5">{t.category}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.short}</p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>
        <section className="container-page pb-20 border-t border-border pt-16">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <EyebrowLabel>What this collection covers</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
                A working vocabulary, not a copy-pasted dictionary.
              </h2>
              <div className="mt-6 space-y-4 text-foreground/85 leading-relaxed">
                <p>
                  Search vocabulary has fractured faster than most reference sites have kept
                  up with. Classic SEO terms — crawl budget, canonical tag, domain authority —
                  now sit alongside AI-search terms like AEO, GEO, and AI Overviews citation
                  behaviour, and the two vocabularies overlap in ways older glossaries don't
                  capture. This page tracks both.
                </p>
                <p>
                  It's written for people who hit an unfamiliar term mid-conversation and need
                  a precise answer, not a marketing paragraph: an in-house marketer briefing a
                  developer, a business owner reading an agency proposal, or a junior
                  practitioner building their own vocabulary. Each definition is short enough
                  to read in under a minute and specific enough to actually settle an argument.
                </p>
                <p>
                  Categories — AI search, technical, on-page, off-page, local, analytics, and
                  content — group terms by the part of an SEO programme they belong to, so you
                  can browse a discipline rather than the alphabet if that's more useful.
                </p>
              </div>
            </div>
            <div>
              <EyebrowLabel>Editorial standards</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                How definitions are researched and maintained
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  ["Practitioner-written, not scraped", "Every definition is drafted by someone who has applied the concept on a live account, then checked against the current, official source — Google's documentation, schema.org, or the relevant standards body."],
                  ["One example per term", "Wherever a term benefits from a concrete illustration, we include one drawn from real work rather than a hypothetical scenario."],
                  ["Cross-referenced, not isolated", "Related terms are linked explicitly — E-E-A-T links to trust signals and author bios, canonical tag links to duplicate content — so you build a map, not a list."],
                  ["Revised when the underlying concept changes", "When Google or a standards body changes what a term means in practice, we rewrite the definition rather than appending a caveat at the bottom."],
                  ["No invented jargon", "We don't coin new terminology to sound proprietary; every entry reflects vocabulary genuinely in use across the industry or in official documentation."],
                ].map(([h, b]) => (
                  <li key={h} className="rounded-xl border border-border/70 bg-background/40 p-5">
                    <div className="font-display text-base tracking-tight">{h}</div>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{b}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="container-page pb-20">
          <EyebrowLabel>Suggested reading order</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Building vocabulary in the right sequence
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
            The glossary is designed for lookup, not linear reading, but if you're building SEO
            literacy from scratch, this order avoids the most common confusion points:
          </p>
          <ol className="mt-6 grid gap-4 md:grid-cols-3">
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">Step 1 — Core mechanics</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Start with the technical category — crawling, indexation, Core Web Vitals —
                since nearly every other term assumes you understand these first.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">Step 2 — Content and authority</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Move to on-page and off-page terms, including E-E-A-T, to understand how
                content and reputation feed the ranking systems described in step one.
              </p>
            </li>
            <li className="rounded-xl border border-border p-5">
              <div className="text-xs uppercase tracking-widest text-primary">Step 3 — AI search and local</div>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
                Finish with AI-search terms like AEO and GEO, and local-specific terms like
                Google Business Profile — the two fastest-moving corners of the vocabulary.
              </p>
            </li>
          </ol>
        </section>

        <section className="container-page pb-20">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <EyebrowLabel>How this connects</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                The glossary is the foundation layer
              </h2>
              <p className="mt-4 text-foreground/85 leading-relaxed">
                Every <Link to={"/guides/" as any}   className="text-primary hover:underline">pillar guide</Link> and
                most <Link to={"/blog/" as any}   className="text-primary hover:underline">blog posts</Link> assume
                the vocabulary defined here rather than re-explaining it inline — that's why
                the individual term pages link back out to the guide chapters and posts that
                apply the concept in practice. Several terms also map directly to a {" "}
                <Link to={"/services/" as any}   className="text-primary hover:underline">service</Link>: if a
                definition describes a deliverable we build for clients, the term page links
                through to where that work is actually scoped.
              </p>
            </div>
            <div>
              <EyebrowLabel>Common misconceptions</EyebrowLabel>
              <h2 className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
                What people get wrong about SEO terminology
              </h2>
              <ul className="mt-6 space-y-3">
                {[
                  "\u201cAEO and GEO are the same thing with different names.\u201d AEO targets structured answer boxes and voice results; GEO targets how generative AI models synthesise and cite sources \u2014 related, but distinct disciplines.",
                  "\u201cDomain authority is a Google ranking factor.\u201d It's a third-party metric built by an SEO tool vendor, not a number Google uses internally, even though it correlates loosely with ranking strength.",
                  "\u201cE-E-A-T is a score you can check.\u201d It's a set of quality guidelines Google's raters and systems assess qualitatively, not a numeric score you can look up anywhere.",
                  "\u201cCore Web Vitals thresholds are fixed forever.\u201d Google has changed the specific metrics measured \u2014 Cumulative Layout Shift and the retirement of First Input Delay in favour of Interaction to Next Paint are recent examples.",
                  "\u201cSchema markup guarantees rich results.\u201d Structured data makes a page eligible for a rich result; it doesn't guarantee the SERP will actually render one.",
                ].map((t, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground/85 leading-relaxed">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="container-page pb-32">
          <EyebrowLabel>FAQ</EyebrowLabel>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Questions readers ask about the glossary
          </h2>
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            {GLOSSARY_FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border p-6">
                <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                <dd className="mt-2 text-sm text-foreground/85 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
